import { useState, useCallback, useContext, useRef, useEffect } from 'react';
import AppContext from '../contexts/AppContext';

const getInitialState = (user) => ({
  // Contract selection (mirrors prebooking flow)
  selectedContractId: null,
  selectedContractName: '',

  // Step 1: Order details
  fromLanguageId: null,
  toLanguageId: null,
  attachments: [],
  instructionsForTranslator: '',
  desiredDeliveryDate: null,
  externalOrderId: '',
  // Translator authorization levels from the backend (TUL-5330), all
  // preselected by default.
  translationLevels: user?.translationLevels || [],
  translatorLevelIds: (user?.translationLevels || []).map((level) => level.id),

  // Step 2: Invoicing and assignment details
  invoiceReferences: [],
  additionalInvoicingInfo: '',
  ccEmails: [],
  costAllocation: null,
  costAllocationGroup: null,

  // Read-only from user context
  customerNumber: user?.phoneNumber || '',
  orderMadeBy: user?.username || '',
  orderingUnit: user?.userGroup?.name || '',
  selectableLanguages: user?.selectableLanguages || [],
  targetLanguages: user?.targetLanguages || [],
});

const useTranslationOrderState = () => {
  const { user } = useContext(AppContext);
  const [formData, setFormData] = useState(getInitialState(user));
  const fileRefsMap = useRef(new Map());

  // Sync read-only user fields when user context loads/changes
  useEffect(() => {
    if (!user) return;
    setFormData((prev) => {
      const translationLevels = user.translationLevels || [];
      return {
        ...prev,
        customerNumber: user.phoneNumber || '',
        orderMadeBy: user.username || '',
        orderingUnit: user.userGroup?.name || '',
        selectableLanguages: user.selectableLanguages || [],
        targetLanguages: user.targetLanguages || [],
        translationLevels,
        // Preselect all levels once they arrive; keep the customer's own
        // selection on later user refreshes.
        translatorLevelIds: prev.translationLevels.length === 0 && translationLevels.length > 0
          ? translationLevels.map((level) => level.id)
          : prev.translatorLevelIds,
      };
    });
  }, [user]);

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updateFields = useCallback((updates) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const generateAttachmentId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `att-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const addAttachment = useCallback((file) => {
    const id = generateAttachmentId();
    fileRefsMap.current.set(id, file);
    setFormData((prev) => ({
      ...prev,
      attachments: [
        ...prev.attachments,
        {
          id,
          filename: file.name,
          contentType: file.type,
          size: file.size,
          estimatedScope: '',
          scopeType: null,
        },
      ],
    }));
  }, []);

  const removeAttachment = useCallback((id) => {
    fileRefsMap.current.delete(id);
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((att) => att.id !== id),
    }));
  }, []);

  const updateAttachmentScope = useCallback((id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.map((att) =>
        att.id === id ? { ...att, [field]: value } : att,
      ),
    }));
  }, []);

  const getFile = useCallback((id) => {
    return fileRefsMap.current.get(id) || null;
  }, []);

  const addEmail = useCallback((email) => {
    setFormData((prev) => {
      if (prev.ccEmails.includes(email)) return prev;
      return {
        ...prev,
        ccEmails: [...prev.ccEmails, email],
      };
    });
  }, []);

  const removeEmail = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      ccEmails: prev.ccEmails.filter((_, i) => i !== index),
    }));
  }, []);

  const resetForm = useCallback(() => {
    fileRefsMap.current.clear();
    setFormData(getInitialState(user));
  }, [user]);

  return {
    formData,
    updateField,
    updateFields,
    addAttachment,
    removeAttachment,
    updateAttachmentScope,
    getFile,
    addEmail,
    removeEmail,
    resetForm,
  };
};

export default useTranslationOrderState;
